'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { SignInLink } from '@/components/platform/SignInLink'
import { LocaleSwitcher } from '@/components/LocaleSwitcher'
import BrandLogo from '@/components/shell/BrandLogo'
import { Chevron } from '@/components/shell/primitives'

/**
 * Site header: the reference shell nav (public/pixelmojo-rebuild,
 * pixelmojo-home.html `.nav` + `.menu`), with the live routes. The wrapper is
 * display:contents so the sticky nav still sticks to the page. ShellEffects
 * sets data-band on the nav while it sits over an ink or accent sheet.
 *
 * The reference nav is flat. Products, Services and Tools also open a
 * dropdown panel (the live site's sub-pages), drawn in the reference's own
 * vocabulary in src/app/shell.css. Top-level labels stay links to their index
 * pages; the chevron button beside each opens its panel from the keyboard.
 *
 * Below the desktop width the burger opens `.menu`, a full-screen sheet.
 * There the three groups collapse (each lists its "All ..." index page
 * first), so the whole menu and its CTAs fit a phone screen; the group
 * holding the current page opens with it. While it is open the page behind
 * is inert, focus starts in the menu and returns to the burger on Escape.
 */

/**
 * The reference shows `.nav__links` above 1100px
 * (`@media (max-width: 1100px)` in src/app/shell.css hides it). Dropdowns
 * exist only where the desktop nav does, so the JS reads the same width.
 */
const DESKTOP_NAV_MIN_PX = 1101

interface NavChild {
  label: string
  href: string
  description: string
}

interface NavItem {
  label: string
  href: string
  children?: NavChild[]
}

const NAV: NavItem[] = [
  { label: 'Home', href: '/' },
  {
    label: 'Products',
    href: '/products',
    // Order: the overview, then the suite sequence Radar (measure) ->
    // Vector (act) -> Hive (scale) as one uninterrupted run, then the two
    // Radar sub-items last. Radar Sites is a Radar capability rather than a
    // fourth product, so it sits with the sample report and not inside the
    // three-product sequence.
    children: [
      {
        label: 'All Products',
        href: '/products',
        description: 'Overview of Radar, Vector, and Hive',
      },
      {
        label: 'Radar: AI Visibility',
        href: '/platform',
        description: 'DIY AI visibility audit + implementation',
      },
      {
        label: 'Vector: Lead Qualification',
        href: '/vector',
        description: '12-dimension scoring for B2B leads',
      },
      {
        label: 'Hive: AI Co-workers',
        href: '/hive',
        description: 'Multi-agent teams that run operations',
      },
      {
        label: 'Radar Sites',
        href: '/radar-sites',
        description: 'AI website builder, inside Radar',
      },
      {
        label: 'Radar Sample Report',
        href: '/platform/sample-report',
        description: 'A full client audit, published end to end',
      },
    ],
  },
  {
    label: 'Services',
    href: '/services',
    children: [
      {
        label: 'All Services',
        href: '/services',
        description: 'Overview of all 4 service tracks',
      },
      {
        label: 'Consulting',
        href: '/consulting',
        description: 'White-label websites and AI visibility execution',
      },
      {
        label: 'AI Visibility Strategy',
        href: '/services/ai-visibility-strategy',
        description: 'GEO, AEO, and LLM optimization sprint + retainer',
      },
      {
        label: 'AI Product Development',
        href: '/services/ai-product-development',
        description: 'Ship AI products in a 90-day launch window',
      },
      {
        label: 'Brand & Sales Design',
        href: '/services/revenue-first-design',
        description: 'Brand identity, design systems, pitch decks',
      },
      {
        label: 'Growth Marketing',
        href: '/services/ai-powered-growth',
        description: 'Pipeline generation for B2B SaaS, retainer-based',
      },
    ],
  },
  {
    label: 'Tools',
    href: '/tools',
    // Radar lives under Products only. Listing it here too, under a second
    // label, read as a different page and split the anchor text for /platform.
    children: [
      {
        label: 'AEO Page Auditor',
        href: '/tools/aeo-page-auditor',
        description: 'Answer engine readiness score',
      },
      {
        label: 'Answer Engine Tester',
        href: '/tools/answer-engine-tester',
        description: 'Test if AI cites your page',
      },
      {
        label: 'AI Citation Tracker',
        href: '/tools/ai-citation-tracker',
        description: 'Check if AI engines cite your brand',
      },
      {
        label: 'All Tools',
        href: '/tools',
        description: 'View all free tools',
      },
    ],
  },
  { label: 'Projects', href: '/projects' },
  { label: 'About', href: '/about' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Blog', href: '/blogs' },
]

const CTA = { label: 'Get in Touch', href: '/contact-us' }

const panelId = (label: string) => `nav-panel-${label.toLowerCase()}`
const menuGroupId = (label: string) => `menu-group-${label.toLowerCase()}`

function Arrow() {
  return (
    <svg
      className='arrow'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      aria-hidden='true'
    >
      <path d='M5 12h14M13 6l6 6-6 6' />
    </svg>
  )
}

export default function Header() {
  const pathname = usePathname()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  // The dropdown opened from its chevron button (keyboard or click). Hover
  // opens panels in CSS; this is the explicit, announced state.
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  // After a navigation the pointer can still rest on the group it clicked
  // through; that group's hover-open is suppressed until the pointer leaves
  // it, so its panel never hangs over the new page.
  const [lockedGroup, setLockedGroup] = useState<string | null>(null)
  // The mobile menu's expanded group (one at a time).
  const [menuGroup, setMenuGroup] = useState<string | null>(null)
  const headerRef = useRef<HTMLElement>(null)
  const burgerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLElement>(null)
  // Whether the mobile menu is open right now, and whether the route change
  // that just happened is what closed it (focus then moves to the new page).
  const menuOpen = useRef(false)
  const closedByNavigation = useRef(false)
  const lastPath = useRef(pathname)

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname.startsWith(href)
  const groupHoldsPage = (item: NavItem) =>
    isActive(item.href) ||
    !!item.children?.some(c => c.href !== '/' && isActive(c.href))

  const openMenu = () => {
    setMenuGroup(NAV.find(groupHoldsPage)?.label ?? null)
    setIsMenuOpen(true)
  }

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close the menu and every dropdown on navigation.
  useEffect(() => {
    if (menuOpen.current) closedByNavigation.current = true
    setIsMenuOpen(false)
    setOpenGroup(null)
    if (lastPath.current === pathname) return
    lastPath.current = pathname
    const hovered =
      headerRef.current?.querySelector<HTMLElement>('.nav__group:hover')
    setLockedGroup(hovered?.dataset.group ?? null)
  }, [pathname])

  // While the mobile menu is open: the page behind can't scroll or take
  // focus, focus starts on the menu's first item, Escape closes it and hands
  // focus back to the burger, and widening to the desktop nav closes it.
  useEffect(() => {
    if (!isMenuOpen) return
    menuOpen.current = true
    const root = document.documentElement
    const behind = Array.from(
      document.querySelectorAll<HTMLElement>('#main-content, footer.footer')
    )
    root.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    behind.forEach(el => (el.inert = true))
    const focusFirst = requestAnimationFrame(() =>
      menuRef.current?.querySelector<HTMLElement>('a, button')?.focus()
    )
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setIsMenuOpen(false)
      burgerRef.current?.focus()
    }
    const desktop = window.matchMedia(`(min-width: ${DESKTOP_NAV_MIN_PX}px)`)
    const onWidth = () => {
      if (!desktop.matches) return
      // The burger hides at this width, so focus goes to the logo rather
      // than staying in the menu that is about to hide.
      if (menuRef.current?.contains(document.activeElement))
        headerRef.current?.querySelector<HTMLElement>('.brand')?.focus()
      setIsMenuOpen(false)
    }
    document.addEventListener('keydown', onKey)
    desktop.addEventListener('change', onWidth)
    return () => {
      cancelAnimationFrame(focusFirst)
      root.style.overflow = ''
      document.body.style.overflow = ''
      behind.forEach(el => (el.inert = false))
      document.removeEventListener('keydown', onKey)
      desktop.removeEventListener('change', onWidth)
      menuOpen.current = false
      // A menu link navigated: start the next page's focus at its content,
      // not on a link inside the closed menu.
      if (closedByNavigation.current) {
        closedByNavigation.current = false
        document.getElementById('main-content')?.focus({ preventScroll: true })
      }
    }
  }, [isMenuOpen])

  // An open dropdown closes on Escape (focus returns to its button), on a
  // click outside the header, and when the window drops below the desktop
  // nav width.
  useEffect(() => {
    if (!openGroup) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const button = document.querySelector<HTMLButtonElement>(
        `[aria-controls="${panelId(openGroup)}"]`
      )
      setOpenGroup(null)
      button?.focus()
    }
    const onPointer = (e: PointerEvent) => {
      if (!headerRef.current?.contains(e.target as Node)) setOpenGroup(null)
    }
    const desktop = window.matchMedia(`(min-width: ${DESKTOP_NAV_MIN_PX}px)`)
    const onWidth = () => {
      if (!desktop.matches) setOpenGroup(null)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    desktop.addEventListener('change', onWidth)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
      desktop.removeEventListener('change', onWidth)
    }
  }, [openGroup])

  return (
    <div className='shell contents'>
      <header
        ref={headerRef}
        className={`nav${isScrolled ? ' is-scrolled' : ''}`}
        id='nav'
        data-header-bar
      >
        <div className='container nav__inner'>
          <Link className='brand' href='/' aria-label='Pixelmojo home'>
            <BrandLogo />
          </Link>
          <nav className='nav__links' aria-label='Primary'>
            {NAV.map(item =>
              item.children ? (
                <div
                  key={item.href}
                  className='nav__group'
                  data-group={item.label}
                  data-open={openGroup === item.label ? '' : undefined}
                  data-hover-lock={lockedGroup === item.label ? '' : undefined}
                  onMouseEnter={() => {
                    if (openGroup && openGroup !== item.label)
                      setOpenGroup(null)
                  }}
                  onMouseLeave={() => {
                    if (lockedGroup === item.label) setLockedGroup(null)
                  }}
                  // Keyboard: tabbing out of a clicked-open group closes it,
                  // so its panel never hangs over the next nav item.
                  onBlur={e => {
                    if (
                      openGroup === item.label &&
                      !e.currentTarget.contains(e.relatedTarget as Node | null)
                    )
                      setOpenGroup(null)
                  }}
                >
                  <Link
                    href={item.href}
                    aria-current={isActive(item.href) ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                  <button
                    type='button'
                    className='nav__toggle'
                    aria-expanded={openGroup === item.label}
                    aria-controls={panelId(item.label)}
                    aria-label={`${item.label} menu`}
                    onClick={() =>
                      setOpenGroup(open =>
                        open === item.label ? null : item.label
                      )
                    }
                  >
                    <Chevron />
                  </button>
                  <div className='nav__panel' id={panelId(item.label)}>
                    <span className='nav__panel-label'>{item.label}</span>
                    <ul>
                      {item.children.map(child => (
                        <li key={child.label}>
                          <Link
                            className='nav__item'
                            href={child.href}
                            aria-current={
                              pathname === child.href ? 'page' : undefined
                            }
                          >
                            <span className='nav__item-text'>
                              <span className='nav__item-title'>
                                {child.label}
                              </span>
                              <span className='nav__item-desc'>
                                {child.description}
                              </span>
                            </span>
                            <Arrow />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.href}
                  href={item.href}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>
          <div className='nav__right'>
            <LocaleSwitcher className='nav__locale' />
            <SignInLink className='nav__signin' />
            {/* Outline (secondary) at the top of the page, so it does not
                compete with the hero's primary CTA; magenta (primary) once
                the page scrolls (Lloyd, 2026-10-09). The colour change
                animates with every .btn's transition (cta.css). */}
            <Link
              className={`btn btn--sm${isScrolled ? '' : ' btn--ghost'}`}
              href={CTA.href}
              data-cta-id='header'
            >
              {CTA.label} <Arrow />
            </Link>
            <button
              ref={burgerRef}
              type='button'
              className='nav__burger'
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls='menu'
              onClick={() => (isMenuOpen ? setIsMenuOpen(false) : openMenu())}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>
      <nav
        ref={menuRef}
        className={`menu${isMenuOpen ? ' is-open' : ''}`}
        id='menu'
        aria-label='Mobile'
        inert={!isMenuOpen}
      >
        {NAV.map(item =>
          item.children ? (
            <div key={item.href} className='menu__group'>
              <button
                type='button'
                className='menu__toggle'
                aria-expanded={menuGroup === item.label}
                aria-controls={menuGroupId(item.label)}
                onClick={() =>
                  setMenuGroup(open =>
                    open === item.label ? null : item.label
                  )
                }
              >
                {item.label}
                <Chevron />
              </button>
              <ul
                id={menuGroupId(item.label)}
                className='menu__sub'
                hidden={menuGroup !== item.label}
              >
                {item.children.map(child => (
                  <li key={child.label}>
                    <Link
                      href={child.href}
                      aria-current={
                        pathname === child.href ? 'page' : undefined
                      }
                    >
                      {child.label}
                      <Arrow />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <Link
              key={item.href}
              href={item.href}
              aria-current={isActive(item.href) ? 'page' : undefined}
            >
              {item.label}
            </Link>
          )
        )}
        <div className='menu__cta'>
          <Link className='btn' href={CTA.href} data-cta-id='header'>
            {CTA.label}
          </Link>
          <SignInLink
            className='btn btn--ghost'
            signedOutLabel='Sign in to Radar'
            signedInLabel='Open Account'
          />
        </div>
      </nav>
    </div>
  )
}
